import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Connection,
  Edge,
  addEdge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  ReactFlowInstance,
  BackgroundVariant,
  NodeMouseHandler,
  EdgeMouseHandler,
  useReactFlow,
  useStore,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ViewColumnsIcon,
  ArrowsPointingOutIcon,
  CpuChipIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';

import { nodeTypes } from './nodes';
// ONE layout implementation, shared with the default applied on load (task 771).
import { getLayoutedElements } from '../../utils/definitionTransform';
import type { ValidationErrorDetail } from '../../services/api';
import { NodeToolbar } from './NodeToolbar';
import { PropertiesPanel } from './PropertiesPanel';
import {
  CollaborationDefinition,
  CollaborationNode,
  CollaborationEdge,
  NodeType,
} from '../../types/definition';
import {
  collaborationToReactFlow,
  reactFlowToCollaboration,
  createNewNode,
  collaborationNodeToReactFlow,
  createEmptyDefinition,
  isGraphDefinition,
  DefinitionNode,
  DefinitionEdge,
} from '../../utils/definitionTransform';
import { validateDefinition, ValidationResult } from '../../utils/definitionValidation';
import { useUndoRedo } from '../../hooks/useUndoRedo';
import {
  DEFINITION_CANVAS_EDIT_MIN_HEIGHT,
  PATH_CANVAS_MIN_HEIGHT,
  PATH_FIT_MAX_ZOOM,
  PATH_FIT_PADDING,
  graphExtent,
  pathCanvasHeight,
} from '../../utils/graphCanvasHeight';

interface DefinitionCanvasProps {
  value: CollaborationDefinition | Record<string, unknown> | null;
  onChange: (definition: CollaborationDefinition) => void;
  className?: string;
  /**
   * Field-level rejections from the last failed save (task 774). The service
   * names them by body path — `nodes[5].inputMapping.targetSummary` — which is
   * resolved back to a node id below using the definition's node order.
   */
  serverErrors?: ValidationErrorDetail[];
  /**
   * Show the graph, change nothing (tasks 807, 881).
   *
   * The config surfaces present a definition as a READ-ONLY VIEW before anyone
   * asks to edit it, and the graph is how a collaboration is read — JSON is
   * not. So the same canvas renders, at the layout the definition stores, with
   * every mutation affordance removed rather than merely styled away:
   *
   *   - the flow library's mutation flags are off (no dragging, no connecting,
   *     no reconnecting), so a drag cannot move a node in the first place;
   *   - the node palette, undo/redo and auto-layout are not rendered — every
   *     one of them writes;
   *   - the change handlers are the library's raw state setters, so nothing
   *     reaches `onChange` even if the library emits an internal measurement
   *     change on mount.
   *
   * SELECTION IS NOT A MUTATION (task 881). It used to be off with the rest,
   * which made the commonest reason to open a graph — reading what a node is
   * wired to — require an edit session. Clicking a node or an edge now selects
   * it and opens the properties panel in its inspect-only presentation, where
   * every field is disabled and no change handler is attached. Nothing about
   * that writes, and `onChange` still never fires.
   *
   * Navigation survives: pan, zoom, fit-view and the minimap all still work,
   * because reading a graph you cannot move around is not reading it.
   */
  readOnly?: boolean;
}

/** One row of the errors panel, whichever validator produced it. */
export interface CanvasIssue {
  severity: 'error' | 'warning';
  /** Absent for graph-level problems, which the panel labels "Definition". */
  nodeId?: string;
  nodeName?: string;
  field: string;
  message: string;
  source: 'client' | 'server';
}

/**
 * Map a server error's body path back to the node it is about.
 *
 * The service addresses nodes positionally (`nodes[5].…`), so the definition's
 * own node order is the key. A path that names no node — or an index past the
 * end, which happens when the save that failed is older than the current
 * graph — resolves to undefined and renders as a definition-level row rather
 * than being silently dropped.
 */
export const resolveServerIssue = (
  detail: ValidationErrorDetail,
  nodes: CollaborationNode[]
): CanvasIssue => {
  const match = /^nodes\[(\d+)\]/.exec(detail.field || '');
  const node = match ? nodes[Number(match[1])] : undefined;
  return {
    severity: 'error',
    nodeId: node?.id,
    nodeName: node?.name || node?.id,
    field: detail.field,
    message: detail.message,
    source: 'server',
  };
};

type Selection =
  | { type: 'node'; node: CollaborationNode }
  | { type: 'edge'; edge: CollaborationEdge; sourceNodeName?: string; targetNodeName?: string };

// Auto-layout using dagre

function DefinitionCanvasInner({
  value,
  onChange,
  className,
  serverErrors,
  readOnly = false,
}: DefinitionCanvasProps) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<ReactFlowInstance<DefinitionNode, DefinitionEdge> | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [propertiesPanelCollapsed, setPropertiesPanelCollapsed] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const { fitView, getNodes, getEdges } = useReactFlow();

  // Convert the input value to a proper definition
  const initialDefinition = useMemo(() => {
    if (isGraphDefinition(value)) {
      return value;
    }
    return createEmptyDefinition();
  }, []);

  // Undo/Redo support
  const { pushState, undo, redo, canUndo, canRedo } = useUndoRedo<CollaborationDefinition>(
    initialDefinition,
    { maxHistory: 50, debounceMs: 500 }
  );

  // Convert definition to ReactFlow format
  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => collaborationToReactFlow(initialDefinition),
    [initialDefinition]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Run validation when nodes/edges change
  useEffect(() => {
    const definition = reactFlowToCollaboration(
      nodes as DefinitionNode[],
      edges as DefinitionEdge[],
      isGraphDefinition(value) ? value : null
    );
    const result = validateDefinition(definition);
    setValidationResult(result);

    // Update nodes with validation errors
    setNodes((nds) =>
      nds.map((node) => {
        const nodeErrors = result.nodeErrors.get(node.id) || [];
        return {
          ...node,
          data: {
            ...node.data,
            validationErrors: nodeErrors,
          },
        };
      })
    );
  }, [nodes.length, edges.length, setNodes]);

  // Notify parent of changes
  const notifyChange = useCallback(
    (newNodes: DefinitionNode[], newEdges: DefinitionEdge[]) => {
      const definition = reactFlowToCollaboration(
        newNodes,
        newEdges,
        isGraphDefinition(value) ? value : null
      );
      onChange(definition);
      pushState(definition);
    },
    [onChange, value, pushState]
  );

  // Handle node changes and notify parent
  const handleNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChange>[0]) => {
      onNodesChange(changes);
      setTimeout(() => {
        setNodes((currentNodes) => {
          notifyChange(currentNodes as DefinitionNode[], edges as DefinitionEdge[]);
          return currentNodes;
        });
      }, 0);
    },
    [onNodesChange, notifyChange, edges, setNodes]
  );

  // Handle edge changes and notify parent
  const handleEdgesChange = useCallback(
    (changes: Parameters<typeof onEdgesChange>[0]) => {
      onEdgesChange(changes);
      setTimeout(() => {
        setEdges((currentEdges) => {
          notifyChange(nodes as DefinitionNode[], currentEdges as DefinitionEdge[]);
          return currentEdges;
        });
      }, 0);
    },
    [onEdgesChange, notifyChange, nodes, setEdges]
  );

  // Handle new connections
  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge: Edge = {
        ...params,
        id: `edge-${Date.now()}`,
        type: 'smoothstep',
      } as Edge;

      setEdges((eds) => {
        const updatedEdges = addEdge(newEdge, eds);
        notifyChange(nodes as DefinitionNode[], updatedEdges as DefinitionEdge[]);
        return updatedEdges;
      });
    },
    [setEdges, notifyChange, nodes]
  );

  // Handle drop from toolbar
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const nodeType = event.dataTransfer.getData('application/reactflow') as NodeType;
      if (!nodeType) return;

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds || !reactFlowInstance.current) return;

      const position = reactFlowInstance.current.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = createNewNode(nodeType, position);
      const reactFlowNode = collaborationNodeToReactFlow(newNode);

      setNodes((nds) => {
        const updatedNodes = nds.concat(reactFlowNode);
        notifyChange(updatedNodes as DefinitionNode[], edges as DefinitionEdge[]);
        return updatedNodes;
      });
    },
    [setNodes, notifyChange, edges]
  );

  const onInit = useCallback((instance: ReactFlowInstance<DefinitionNode, DefinitionEdge>) => {
    reactFlowInstance.current = instance;
  }, []);

  // Handle node click
  const onNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    const definitionNode = node as DefinitionNode;
    setSelection({
      type: 'node',
      node: definitionNode.data.originalNode,
    });
  }, []);

  // Handle edge click
  const onEdgeClick: EdgeMouseHandler = useCallback((_event, edge) => {
    const definitionEdge = edge as DefinitionEdge;
    const originalEdge = definitionEdge.data?.originalEdge as CollaborationEdge | undefined;

    const sourceNode = nodes.find(n => n.id === edge.source) as DefinitionNode | undefined;
    const targetNode = nodes.find(n => n.id === edge.target) as DefinitionNode | undefined;

    setSelection({
      type: 'edge',
      edge: originalEdge || {
        id: edge.id,
        from: edge.source,
        to: edge.target,
      },
      sourceNodeName: sourceNode?.data.label,
      targetNodeName: targetNode?.data.label,
    });
  }, [nodes]);

  // Handle pane click
  const onPaneClick = useCallback(() => {
    setSelection(null);
  }, []);

  // Handle property changes from the panel
  const handleNodePropertyChange = useCallback(
    (nodeId: string, updates: Partial<CollaborationNode>) => {
      setNodes((nds) => {
        const updatedNodes = nds.map((node) => {
          if (node.id === nodeId) {
            const definitionNode = node as DefinitionNode;
            const updatedOriginalNode = {
              ...definitionNode.data.originalNode,
              ...updates,
            } as CollaborationNode;

            setSelection((prev) => {
              if (prev?.type === 'node' && prev.node.id === nodeId) {
                return { type: 'node', node: updatedOriginalNode };
              }
              return prev;
            });

            return {
              ...node,
              data: {
                ...definitionNode.data,
                label: updatedOriginalNode.name || definitionNode.data.label,
                originalNode: updatedOriginalNode,
              },
            };
          }
          return node;
        });

        notifyChange(updatedNodes as DefinitionNode[], edges as DefinitionEdge[]);
        return updatedNodes;
      });
    },
    [setNodes, edges, notifyChange]
  );

  const handleEdgePropertyChange = useCallback(
    (edgeId: string, updates: Partial<CollaborationEdge>) => {
      setEdges((eds) => {
        const updatedEdges = eds.map((edge) => {
          if (edge.id === edgeId) {
            const originalEdge = (edge.data?.originalEdge as CollaborationEdge) || {
              id: edge.id,
              from: edge.source,
              to: edge.target,
            };
            const updatedOriginalEdge = { ...originalEdge, ...updates };

            setSelection((prev) => {
              if (prev?.type === 'edge' && prev.edge.id === edgeId) {
                return { ...prev, edge: updatedOriginalEdge };
              }
              return prev;
            });

            return {
              ...edge,
              label: updates.label ?? edge.label,
              data: {
                ...edge.data,
                originalEdge: updatedOriginalEdge,
              },
            };
          }
          return edge;
        });

        notifyChange(nodes as DefinitionNode[], updatedEdges as DefinitionEdge[]);
        return updatedEdges;
      });
    },
    [setEdges, nodes, notifyChange]
  );

  const handleClosePanel = useCallback(() => {
    setSelection(null);
  }, []);

  const handleTogglePanel = useCallback(() => {
    setPropertiesPanelCollapsed((prev) => !prev);
  }, []);

  // Auto-layout handler
  const handleAutoLayout = useCallback(() => {
    const currentNodes = getNodes() as DefinitionNode[];
    const currentEdges = getEdges() as DefinitionEdge[];

    const { nodes: layoutedNodes } = getLayoutedElements(currentNodes, currentEdges, 'LR');

    setNodes(layoutedNodes);
    notifyChange(layoutedNodes, currentEdges);

    setTimeout(() => fitView({ padding: 0.2 }), 50);
  }, [getNodes, getEdges, setNodes, notifyChange, fitView]);

  // Undo handler
  const handleUndo = useCallback(() => {
    const previousState = undo();
    if (previousState) {
      const { nodes: newNodes, edges: newEdges } = collaborationToReactFlow(previousState);
      setNodes(newNodes);
      setEdges(newEdges);
      onChange(previousState);
    }
  }, [undo, setNodes, setEdges, onChange]);

  // Redo handler
  const handleRedo = useCallback(() => {
    const nextState = redo();
    if (nextState) {
      const { nodes: newNodes, edges: newEdges } = collaborationToReactFlow(nextState);
      setNodes(newNodes);
      setEdges(newEdges);
      onChange(nextState);
    }
  }, [redo, setNodes, setEdges, onChange]);

  // Keyboard shortcuts
  useEffect(() => {
    // Every shortcut below mutates or deselects; a read-only canvas has neither
    // to offer, and Delete must not be reachable from it at all (task 807).
    if (readOnly) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // Delete selected node/edge
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selection?.type === 'node') {
          setNodes((nds) => {
            const updatedNodes = nds.filter(n => n.id !== selection.node.id);
            notifyChange(updatedNodes as DefinitionNode[], edges as DefinitionEdge[]);
            return updatedNodes;
          });
          setSelection(null);
        } else if (selection?.type === 'edge') {
          setEdges((eds) => {
            const updatedEdges = eds.filter(e => e.id !== selection.edge.id);
            notifyChange(nodes as DefinitionNode[], updatedEdges as DefinitionEdge[]);
            return updatedEdges;
          });
          setSelection(null);
        }
      }

      // Escape to deselect
      if (event.key === 'Escape') {
        setSelection(null);
      }

      // Undo: Cmd/Ctrl + Z
      if ((event.metaKey || event.ctrlKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        handleUndo();
      }

      // Redo: Cmd/Ctrl + Shift + Z
      if ((event.metaKey || event.ctrlKey) && event.key === 'z' && event.shiftKey) {
        event.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [readOnly, selection, setNodes, setEdges, nodes, edges, notifyChange, handleUndo, handleRedo]);

  const hasNodes = nodes.length > 0;

  /**
   * HOW TALL THE CANVAS IS (task 1338): the run page's Path card rule (task
   * 1324). The extent of the rendered nodes at the zoom the card's width
   * dictates, clamped between a minimum — 320px to read, 560px to edit, so
   * there is room to place nodes — and min(900px, window − 160px).
   *
   * The extent is recomputed when a node is added or removed or the flow
   * library measures a node's size, NOT on every drag move: a drag changes
   * positions only, so the card does not jump under the pointer while a node
   * is being placed. Auto-layout fits the view itself.
   */
  const [cardWidth, setCardWidth] = useState(0);
  const [windowHeight, setWindowHeight] = useState(() => window.innerHeight);
  const sizingKey = nodes
    .map((node) => `${node.id}:${node.measured?.width ?? 0}x${node.measured?.height ?? 0}`)
    .join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const extent = useMemo(() => graphExtent(nodes), [sizingKey]);
  const canvasHeight = pathCanvasHeight(
    extent,
    cardWidth,
    windowHeight,
    readOnly ? PATH_CANVAS_MIN_HEIGHT : DEFINITION_CANVAS_EDIT_MIN_HEIGHT
  );

  // The card grows to the graph, so it needs its own width and the window's height.
  useEffect(() => {
    const wrapper = reactFlowWrapper.current;
    if (!wrapper) return undefined;
    const measure = () => {
      setCardWidth(wrapper.getBoundingClientRect().width);
      setWindowHeight(window.innerHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [readOnly]);

  // React Flow's own measurement of its pane height. It updates after the card's
  // inline height lands, so the refit keys on it rather than on `canvasHeight`:
  // fitting on `canvasHeight` used the previous pane size (task 1349, the run
  // page's task 1324 fix). The pane WIDTH is left out on purpose: the properties
  // panel narrows the pane when a node is selected, and the view must not refit
  // while the User inspects or drags a node (task 1338).
  const paneHeight = useStore((s) => s.height);

  // The `fitView` prop only fits on mount; refit when the node set or the measured pane height changes.
  const nodeSetKey = nodes.map((node) => node.id).join('|');
  useEffect(() => {
    if (!nodeSetKey) return undefined;
    const frame = requestAnimationFrame(() => {
      void fitView({ padding: PATH_FIT_PADDING, maxZoom: PATH_FIT_MAX_ZOOM, duration: 200 });
    });
    return () => cancelAnimationFrame(frame);
  }, [fitView, nodeSetKey, paneHeight]);

  /** The definition's own nodes — what `Selection` and the issue rows address. */
  const definitionNodes: CollaborationNode[] = useMemo(
    () =>
      value && typeof value === 'object' && 'nodes' in value
        ? ((value as CollaborationDefinition).nodes ?? [])
        : [],
    [value]
  );

  /**
   * The two validators' findings, in one list (task 774).
   *
   * Client-side issues come from `validateDefinition`; server-side ones from
   * the last rejected save. They are deliberately NOT merged or de-duplicated —
   * a User needs to know whether the editor is objecting or the service
   * refused, because only the second one blocked a save.
   */
  const issues: CanvasIssue[] = useMemo(() => {
    const clientIssues: CanvasIssue[] = [
      ...(validationResult?.errors ?? []),
      ...(validationResult?.warnings ?? []),
    ].map((issue) => {
      const node = definitionNodes.find((candidate) => candidate.id === issue.nodeId);
      return {
        severity: issue.severity,
        nodeId: issue.nodeId,
        nodeName: node?.name || issue.nodeId,
        field: issue.field,
        message: issue.message,
        source: 'client' as const,
      };
    });

    return [
      ...(serverErrors ?? []).map((detail) => resolveServerIssue(detail, definitionNodes)),
      ...clientIssues,
    ];
  }, [validationResult, serverErrors, definitionNodes]);

  const errorCount = issues.filter((issue) => issue.severity === 'error').length;
  const warningCount = issues.filter((issue) => issue.severity === 'warning').length;

  /** The selected node's problems, repeated above its fields (task 774). */
  const selectedNodeIssues = useMemo(
    () =>
      selection?.type === 'node'
        ? issues.filter((issue) => issue.nodeId === selection.node.id)
        : [],
    [issues, selection]
  );

  /**
   * Jump to the node a row names: select it (which opens the properties panel
   * on that node, where the same message is repeated above the fields) and pan
   * the canvas to it, so the message and the thing it is about are on screen
   * together.
   */
  const handleIssueClick = useCallback(
    (issue: CanvasIssue) => {
      if (!issue.nodeId) return;
      const node = definitionNodes.find((candidate) => candidate.id === issue.nodeId);
      if (node) {
        setSelection({ type: 'node', node });
        fitView({ nodes: [{ id: issue.nodeId }], duration: 300, maxZoom: 1.2 });
      }
    },
    [definitionNodes, fitView]
  );

  return (
    <div className={`flex ${className || ''}`}>
      <div className="flex-1 flex flex-col min-w-0">
        {/* Toolbar — every control on it writes (drag a node in, undo, redo,
            re-layout), so a read-only canvas renders none of it (task 807).
            Pan, zoom and fit-view survive in the flow library's own `Controls`
            below, which is navigation only. */}
        {!readOnly && (
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
          <NodeToolbar />
          <div className="flex items-center gap-1 px-3 py-2">
            {/* Validation status — a BUTTON (task 774): the counts used to be
                inert text, so the editor could say "2 errors" while offering no
                way to see or reach them. */}
            {hasNodes && (errorCount > 0 || warningCount > 0) && (
              <button
                type="button"
                onClick={() => setIssuesOpen((open) => !open)}
                data-testid="canvas-issues-toggle"
                aria-expanded={issuesOpen}
                title={issuesOpen ? 'Hide problems' : 'Show problems'}
                className="flex items-center gap-2 mr-3 rounded-md px-2 py-1 text-xs hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {errorCount > 0 && (
                  <span className="text-red-600 dark:text-red-400">
                    {errorCount} error{errorCount !== 1 ? 's' : ''}
                  </span>
                )}
                {warningCount > 0 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {warningCount} warning{warningCount !== 1 ? 's' : ''}
                  </span>
                )}
              </button>
            )}

            {/* Undo */}
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Undo (Cmd+Z)"
            >
              <ArrowUturnLeftIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>

            {/* Redo */}
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Redo (Cmd+Shift+Z)"
            >
              <ArrowUturnRightIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>

            <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />

            {/* Auto-layout */}
            <button
              onClick={handleAutoLayout}
              disabled={!hasNodes}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Auto-layout nodes"
            >
              <ViewColumnsIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>

            {/* Fit view */}
            <button
              onClick={() => fitView({ padding: 0.2 })}
              disabled={!hasNodes}
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
              title="Fit to view"
            >
              <ArrowsPointingOutIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
        )}

        {/* Problems panel — docked under the toolbar rather than floating as a
            popover, so it can stay open while the User works through the list
            and never covers the node it just navigated to. */}
        {issuesOpen && issues.length > 0 && (
          <div
            data-testid="canvas-issues-panel"
            className="max-h-48 flex-shrink-0 overflow-y-auto border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40"
          >
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {issues.map((issue, index) => (
                <li key={`${issue.source}-${issue.nodeId ?? 'definition'}-${issue.field}-${index}`}>
                  <button
                    type="button"
                    onClick={() => handleIssueClick(issue)}
                    disabled={!issue.nodeId}
                    data-testid="canvas-issue-row"
                    data-node-id={issue.nodeId ?? ''}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-white disabled:cursor-default dark:hover:bg-gray-800"
                  >
                    <span
                      className={
                        issue.severity === 'error'
                          ? 'flex-shrink-0 font-medium text-red-600 dark:text-red-400'
                          : 'flex-shrink-0 font-medium text-amber-600 dark:text-amber-400'
                      }
                    >
                      {issue.severity === 'error' ? 'Error' : 'Warning'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {issue.nodeName || 'Definition'}
                      </span>
                      <span className="ml-1.5 font-mono text-gray-500 dark:text-gray-400">
                        {issue.field}
                      </span>
                      <span className="block text-gray-700 dark:text-gray-300">{issue.message}</span>
                    </span>
                    {/* Which validator objected: only a server row blocked a save. */}
                    <span
                      data-testid="canvas-issue-source"
                      className="flex-shrink-0 rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    >
                      {issue.source}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Canvas */}
        <div
          ref={reactFlowWrapper}
          data-testid="definition-canvas"
          data-readonly={readOnly ? 'true' : 'false'}
          style={{ height: canvasHeight }}
          className="relative"
        >
          {!hasNodes && (
            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
              <div className="text-center p-8 max-w-md">
                <CpuChipIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {readOnly ? 'Nothing here yet' : 'Build Your Collaboration'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {readOnly
                    ? 'This collaboration has no nodes yet. Choose Edit to build it.'
                    : 'Drag nodes from the toolbar above to start building your collaboration. Connect nodes by dragging from one handle to another.'}
                </p>
              </div>
            </div>
          )}

          <ReactFlow
            nodes={nodes}
            edges={edges}
            /* READ-ONLY (task 807). The change handlers are the library's own
               raw state setters, NOT the wrappers that call `notifyChange` —
               the library still needs them to record node measurements on
               mount, and routing those through the notifying wrappers is
               exactly how a view could have reported itself dirty for having
               been looked at. */
            onNodesChange={readOnly ? onNodesChange : handleNodesChange}
            onEdgesChange={readOnly ? onEdgesChange : handleEdgesChange}
            onConnect={readOnly ? undefined : onConnect}
            onInit={onInit}
            onDrop={readOnly ? undefined : onDrop}
            onDragOver={readOnly ? undefined : onDragOver}
            /* Selection, in BOTH modes (task 881). These three handlers only
               set `selection`, which opens the properties panel — read-only
               when the canvas is, where it is an inspector rather than an
               editor. Clicking to read what a node is wired to is not an edit.
               `onPaneClick` is the way back out of it. */
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            /* The flags that make a drag impossible rather than merely
               ineffective. Pan, zoom and selection are deliberately NOT among
               them: none of the three writes. */
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            nodesFocusable
            elementsSelectable
            edgesFocusable
            edgesReconnectable={!readOnly}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: PATH_FIT_PADDING, maxZoom: PATH_FIT_MAX_ZOOM }}
            snapToGrid={!readOnly}
            snapGrid={[15, 15]}
            deleteKeyCode={null}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false,
            }}
            className="bg-gray-50 dark:bg-gray-900"
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={15}
              size={1}
              className="!bg-gray-50 dark:!bg-gray-900"
            />
            <Controls
              showInteractive={false}
              className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700 !shadow-md"
            />
            {hasNodes && (
              <MiniMap
                nodeStrokeWidth={3}
                zoomable
                pannable
                className="!bg-white dark:!bg-gray-800 !border-gray-200 dark:!border-gray-700"
                nodeColor={(node) => {
                  const type = (node as DefinitionNode).data?.nodeType;
                  switch (type) {
                    case 'entry': return '#22c55e';
                    case 'agent': return '#3b82f6';
                    case 'function': return '#a855f7';
                    case 'branch': return '#f97316';
                    default: return '#6b7280';
                  }
                }}
              />
            )}
          </ReactFlow>
        </div>
      </div>

      {/* Properties Panel — the editor in edit mode, the INSPECTOR in read-only
          (task 881). `readOnly` travels with it so every field inside renders
          disabled with no change handler attached; the panel is otherwise the
          same one, so what a reader sees is what an editor edits. */}
      {/* The panel takes the canvas's height without adding to it (task 1338):
          absolutely positioned inside a column the row stretches, so a long
          node's fields scroll inside the panel rather than lengthening the card. */}
      {selection && (
        <div className={`relative flex-shrink-0 ${propertiesPanelCollapsed ? 'w-10' : 'w-80'}`}>
        <div className="absolute inset-0 flex">
        {propertiesPanelCollapsed ? (
          <div className="w-10 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col items-center py-2">
            <button
              onClick={handleTogglePanel}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
              title="Expand properties panel"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <PropertiesPanel
            selection={selection}
            issues={selectedNodeIssues}
            readOnly={readOnly}
            onClose={handleClosePanel}
            onCollapse={handleTogglePanel}
            onNodeChange={handleNodePropertyChange}
            onEdgeChange={handleEdgePropertyChange}
          />
        )}
        </div>
        </div>
      )}
    </div>
  );
}

export function DefinitionCanvas(props: DefinitionCanvasProps) {
  return (
    <ReactFlowProvider>
      <DefinitionCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
